// context/CouponContext.js
import { createContext, useCallback, useContext, useRef, useState } from "react";
import { fetchCoupons } from "../services/couponService";
import { useUserAuth } from "./UserAuthContext";

const CouponContext = createContext();

export const useCouponContext = () => useContext(CouponContext);

function vendorKeyFromCart(cart) {
  if (!cart?.length) return "";
  return cart
    .map((i) => String(i.product?.vendorId ?? ""))
    .sort()
    .join("|");
}

export const CouponProvider = ({ children }) => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user } = useUserAuth();
  const userId = user?._id;

  /** Avoid duplicate / in-flight fetches for the same vendor set (e.g. two CouponPage mounts). */
  const lastLoadedKeyRef = useRef(null);
  const inFlightKeyRef = useRef(null);

  const loadCoupons = useCallback(
    async (cart, { force = false } = {}) => {
      if (!cart || cart.length === 0) {
        lastLoadedKeyRef.current = null;
        inFlightKeyRef.current = null;
        setCoupons([]);
        return [];
      }

      const key = `${vendorKeyFromCart(cart)}|u:${userId || ""}`;
      if (!force && lastLoadedKeyRef.current === key) {
        return;
      }
      if (!force && inFlightKeyRef.current === key) {
        return;
      }

      inFlightKeyRef.current = key;

      setLoading(true);
      try {
        const res = await fetchCoupons(cart, userId);
        const list = res.coupons || [];
        setCoupons(list);
        setError("");
        lastLoadedKeyRef.current = key;
        return list;
      } catch (err) {
        console.error("Failed to fetch coupons:", err);
        setCoupons([]);
        setError(err.message);
        lastLoadedKeyRef.current = null;
        return [];
      } finally {
        setLoading(false);
        inFlightKeyRef.current = null;
      }
    },
    [userId],
  );

  const refreshCoupons = useCallback(
    (cart) => loadCoupons(cart, { force: true }),
    [loadCoupons],
  );

  return (
    <CouponContext.Provider
      value={{ coupons, loadCoupons, refreshCoupons, loading, error }}
    >
      {children}
    </CouponContext.Provider>
  );
};
